<template>
    <div>
        <b-table id="order-list-table" :fields="fields" :items="paginatedItems" no-select-on-click sort-icon-left
            :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :no-local-sorting="currentPerPage > 0"
            @row-clicked="rowClicked" @sort-changed="onSortChanged" :busy="busy">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle" />
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(id)="data">
                {{ data.value }}
                <b-badge v-if="isOrderUrgent(data.item)" variant="danger" class="ml-1">URGENT</b-badge>
            </template>
            <template #cell(action)="data">
                <div v-if="mergable">
                    <b-button @click="$emit('merge-order', data.item)">merge</b-button>
                </div>
                <div v-else-if="!hideActionsColumn" class="table-icon-actions">
                    <router-link v-if="!audit"
                        :to="{ name: (mode == 'verify' ? 'OrderVerification' : data.item.status == 6 ? 'OrderPickup' : 'OrderDetails'), params: { orderId: data.item.id } }"
                        :title="mode == 'verify' ? 'Order Verification' : 'Order Details'">
                        <b-icon icon="pencil-square"></b-icon>
                    </router-link>
                    <router-link v-if="audit"
                        :to="{ name: 'Audit', params: { id: data.item.storeID + '-' + data.item.rxRfs } }"
                        title="Audit">
                        <b-icon icon="pencil-square"></b-icon>
                    </router-link>
                    <router-link v-if="data.item.requiresAudit"
                        :to="{ name: 'OrderAuditDetails', params: { id: data.item.id } }" title="Audit Details">
                        <b-icon icon="clipboard-check" />
                    </router-link>
                    <a href="#" v-if="data.item.status === OrderStatus.AwaitingPriorAuthorization"
                        @click.prevent="overridePriorAuth(data.item.id)" title="PA Override">
                        <b-icon icon="clock" />
                    </a>
                </div>
            </template>
            <template #cell(promisedDate)="data">
                <PromisedTimeSummary v-if="isPromisedApplicable(data.item.status, data.item.promisedDate)"
                    :value="data.item.promisedDate" size="sm" />
            </template>
            <template #cell(status)="row">
                <span v-if="row.item.status === OrderStatus.Canceled && row.item.cancellationReason"
                    :title="'Cancellation Reason: ' + row.item.cancellationReason" v-b-tooltip.hover>
                    {{ row.value }}
                </span>
                <span v-else :class="{ 'text-danger': row.item.status === OrderStatus.PartialPayment }">{{ row.value }}</span>
                <div v-if="row.item.isPartialFill || row.item.isCompletionOrder" class="mt-1">
                    <b-badge v-if="row.item.isPartialFill" variant="warning"
                        v-b-tooltip.hover title="This order was partially filled">
                        Partial
                    </b-badge>
                    <b-badge v-if="row.item.isCompletionOrder" variant="info"
                        v-b-tooltip.hover title="Completion order for a partial fill">
                        Completion
                    </b-badge>
                </div>
            </template>
            <template #cell(drugDetails)="data">
                <div v-if="data.item.prescriptions?.[0]?.drugName">
                    <div>{{ data.item.prescriptions[0].drugName }}</div>
                    <small class="text-muted">{{ data.item.prescriptions[0].ndC11 }}</small>
                </div>
            </template>
            <template #row-details="row">
                <b-card>
                    <div v-if="row.item.status === OrderStatus.Canceled && row.item.cancellationReason">
                        Cancellation Reason: {{ row.item.cancellationReason }}
                        <hr>
                    </div>
                    <ul class="detail-list mb-1">
                        <li v-for="(value, key) in row.item.prescriptions" :key="key">
                            {{ value.rxId }}: {{ value.state }} - {{ value.productNameShort }}
                            <div v-if="value.error"
                                :class="{ 'text-danger': value.hasUnresolvedErrors, 'text-muted': !value.hasUnresolvedErrors }">
                                {{ value.error }}
                            </div>
                        </li>
                    </ul>
                </b-card>
            </template>
        </b-table>
        <BottomPagerBar
            v-if="currentPerPage > 0 && (!hidePagerWhenNotNeeded || items.length > currentPerPage)"
            v-model="pageNumber"
            :total-rows="items.length"
            :per-page="currentPerPage"
            show-total
            show-page-size-selector
            item-name="order"
            :loading="busy"
            @change-page-size="currentPerPage = $event"
        />
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator';
import { Order, OrderStatus, isPromisedApplicable } from '@/models/Order';
import { BvTableFieldArray, BTable } from "bootstrap-vue";
import moment from "moment";
import { DispenseError } from '@/models/DispenseError';
import PromisedTimeSummary from "@/components/Order/PromisedTimeSummaryComponent.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Axios from 'axios';
import BottomPagerBar from '@/components/BottomPagerBar.vue';

@Component({
    name: "OrdersListComponent",
    components: {
        PromisedTimeSummary,
        BottomPagerBar
    },
})
export default class OrdersListComponent extends Vue {
    @Prop({ default: {} as Array<Order> }) private orders!: Order[];
    @Prop({ default: false, type: Boolean }) protected hideActionsColumn!: boolean;
    @Prop({ default: false, type: Boolean }) private expandable!: boolean;
    @Prop({ default: false, type: Boolean }) protected mergable!: boolean;
    @Prop() private mode!: string;
    @Prop({ default: false }) protected audit!: Boolean;
    @Prop({ default: false }) protected busy!: Boolean;
    @Prop({ default: 0, type: Number }) protected perPage!: number;  // 0 = no pagination
    @Prop({ default: true, type: Boolean }) protected hidePagerWhenNotNeeded!: boolean;

    @Ref("order-list-table") protected readonly table!: BTable;

    // Pagination
    private pageNumber: number = 1;
    private currentPerPage: number = 0;

    // Sorting
    private sortBy: string = '';
    private sortDesc: boolean = false;

    mounted() {
        this.fetchDispenseErrorLabel();
        this.currentPerPage = this.perPage;
    }

    @Watch('perPage')
    onPerPagePropChange(val: number) {
        this.currentPerPage = val;
    }

    @Watch('orders')
    onOrdersChange(newVal: Order[]) {
        const perPage = Math.max(1, this.currentPerPage);
        const maxPage = Math.max(1, Math.ceil((newVal?.length ?? 0) / perPage));
        if (this.pageNumber > maxPage) this.pageNumber = 1;
    }

    /** Reset pagination to page 1 - can be called from parent via ref */
    public goToFirstPage() {
        this.pageNumber = 1;
    }

    onSortChanged() {
        this.pageNumber = 1;
    }

    get items(): any[] {
        return this.orders.map((o: any) => {
            if (o.status == OrderStatus.Canceled) o._rowVariant = "danger";
            else if (o.status == OrderStatus.Shipped) o._rowVariant = "info";
            else if (o.status == OrderStatus.ReadyForPickup) o._rowVariant = "warning";
            else if (o.status == OrderStatus.Error) o._rowVariant = "dark";

            return o;
        });
    }

    get sortedItems(): any[] {
        const items = [...this.items];
        if (!this.sortBy) {
            return items;
        }

        items.sort((a, b) => {
            let aVal: any;
            let bVal: any;

            // Special handling for drugDetails - sort by nested drugName
            if (this.sortBy === 'drugDetails') {
                aVal = a.prescriptions?.[0]?.drugName ?? '';
                bVal = b.prescriptions?.[0]?.drugName ?? '';
            } else {
                aVal = a[this.sortBy];
                bVal = b[this.sortBy];
            }

            // Handle null/undefined
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            let result: number;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                result = aVal - bVal;
            } else if (aVal instanceof Date && bVal instanceof Date) {
                result = aVal.getTime() - bVal.getTime();
            } else {
                result = String(aVal).localeCompare(String(bVal));
            }

            return this.sortDesc ? -result : result;
        });

        return items;
    }

    get paginatedItems(): any[] {
        if (!this.currentPerPage || this.currentPerPage <= 0) {
            return this.sortedItems;
        }
        const start = (this.pageNumber - 1) * this.currentPerPage;
        return this.sortedItems.slice(start, start + this.currentPerPage);
    }

    get hasPromised(): boolean {
        return !!this.items?.find(i => isPromisedApplicable(i.status, i.promisedDate));
    }

    get hasCentralFill(): boolean {
        return !!this.items?.find((i: Order) => i.centralFillId?.length > 0);
    }

    get hasDrugInfo(): boolean {
        return !!this.items?.find((i: Order) => i.prescriptions?.[0]?.drugName);
    }

    isPromisedApplicable = isPromisedApplicable;

    get fields(): BvTableFieldArray {
        const fields = [];
        if (this.hasCentralFill) {
            fields.push({
                key: "centralFillId",
                label: "Central Fill",
                formatter: (c?: string) => `${ (c?.length ?? 0) > 10 ? c?.substring(0, 8) + '...' : c }`,
                sortable: true,
            });
        }
        fields.push({ key: "id", label: "Order Id", });
        if (this.mode === "verify") {
            fields.push({ key: "patientName", label: "Patient", class: "patient-col", sortable: true });
            if (this.hasDrugInfo) {
                fields.push({ key: "drugDetails", label: "Drug", sortable: true, sortByFormatted: true, formatter: this.drugDetailsFormatter });
            }
        }
        fields.push({
            key: "created",
            label: "Date",
            formatter: (d: any) => moment.utc(d).format("L"),
            sortable: true,
        });

        if (this.hasPromised) {
            fields.push({ key: "promisedDate", label: "Promised", sortable: true });
        }

        fields.push({ key: "createdBy", label: "Created By", formatter: (c: any) => c.split("@")[0] });
        fields.push({ key: "rxRfs", label: "Rx-Refill", });
        fields.push({ key: "storeName", label: "Store", });
        fields.push({ key: "status", formatter: this.orderStatusName });
        fields.push({ key: "action", label: "", class: "action-col", });
        return fields;
    }

    orderStatusName(enumNbr: number): string {
        return OrderStatus[enumNbr];
    }

    drugDetailsFormatter(_value: any, _key: string, item: Order): string {
        const firstPrescription = item.prescriptions?.[0];
        return firstPrescription?.drugName ?? '';
    }

    formattedDate(value: Date | null): string {
        if (value != null) {
            const date = moment.utc(value).local();
            return date.format("MM-DD-YYYY");
        }
        return "";
    }

    isOrderUrgent(item: any): boolean {
        return !!(item?.isUrgent || item?.prescriptions?.some((rx: any) => rx?.isUrgent));
    }

    rowClicked(item: any, _index: number, _event: Event) {
        if (this.mode == 'verify') {
            this.$router.push({ name: 'OrderVerification', params: { orderId: item.id } })
        }
        if (!this.expandable) return;

        const prescriptions: any[] = item.prescriptions;

        if (!prescriptions?.length) {
            this.$set(item, "_showDetails", !item._showDetails);
            return;
        }

        const requests: Promise<void>[] = [];
        prescriptions.filter(p => p.rxId).forEach(prescription => {
            requests.push(Axios.get<DispenseError[]>(`DispenseError/By-RxId/${prescription.rxId}`)
                .then(response => {
                    const dispenseErrors: DispenseError[] = response.data.map((err: DispenseError) => Object.assign(new DispenseError(), err));
                    if (!dispenseErrors?.length) return;

                    const totalErrorCount = dispenseErrors.length;
                    const unresolvedErrors = dispenseErrors.filter(e => !e.resolved);
                    const unresolvedErrorCount = unresolvedErrors.length;

                    prescription.hasUnresolvedErrors = !!unresolvedErrorCount;

                    prescription.error = unresolvedErrors
                        .map(err => `(${moment.utc(err.updated).format("MM/DD/yy hh:mm:ss a")}) ${err.reason}: ${err.errorTypeDesc}`)
                        .join('\n');

                    const resolvedErrorCount = totalErrorCount - unresolvedErrorCount;

                    if (resolvedErrorCount) {
                        prescription.error += `(Hiding ${resolvedErrorCount} resolved ${this.dispenseErrorLabel}${resolvedErrorCount > 1 ? 's' : ''})`;
                    }
                })
                .catch(error => {
                    console.error("Error while getting error", {
                        error,
                        response: error?.response
                    });
                    this.$notification(NotificationOptions.error(error));
                }));
        });
        Promise.all(requests).finally(() => {
            this.$set(item, "_showDetails", !item._showDetails);
        });
    }

    OrderStatus = OrderStatus;

    async overridePriorAuth(orderId: number) {
        try {
            const msg = `Are you sure you want to override the Awaiting Prior Authorization status for this order?`;
            const confirmed = await this.$bvModal.msgBoxConfirm(msg, {
                title: 'Override Awaiting Prior Authorization',
                okVariant: 'info',
                okTitle: 'Override',
                cancelTitle: 'Cancel',
                centered: true,
                noFade: true
            });

            if (!confirmed) return;

            await Axios.post(`/Order/override-pa/${orderId}`);

            this.$emit("change");
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }
}

</script>

<style scoped>
::v-deep(.action-col) {
    width: 30px;
}

::v-deep(.detail-list ::marker),
::v-deep(.detail-list li) {
    color: black;
}
</style>
